import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  LocationCity,
  Add,
  FolderOpen,
  Schedule,
  Description,
  ArrowForward,
  Delete,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../hooks/useProjects';
import { Project } from '../types/project';
import { projectsApi } from '../services/api';
import UserMenu from '../components/UserMenu';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'completed':
      return 'primary';
    case 'archived':
      return 'default';
    default:
      return 'default';
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function ViewProjects() {
  const navigate = useNavigate();
  const { projects, loading, error, fetchProjects } = useProjects();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleCreateProject = () => {
    navigate('/create-project');
  };

  const handleOpenProject = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleDeleteCancel = () => {
    setDeleteDialog(false);
    setSelectedProject(null);
    setDeleteConfirmText('');
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProject || deleteConfirmText !== 'DELETE') return;

    setDeleting(true);
    try {
      await projectsApi.deleteProject(selectedProject.id);
      await fetchProjects(); // Refresh the list
      setDeleteDialog(false);
      setSelectedProject(null);
      setDeleteConfirmText('');
    } catch (err) {
      console.error('Failed to delete project:', err);
      // Error handling could be improved here
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', boxShadow: 1 }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationCity sx={{ color: 'primary.main', fontSize: 28 }} />
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    bgcolor: 'secondary.main',
                    borderRadius: '50%',
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: 'primary.main',
                    borderRadius: '2px',
                    transform: 'rotate(45deg)',
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ ml: 2, fontWeight: 700, color: 'primary.main' }}>
                CivilSteve
              </Typography>
              <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
                Projects
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <UserMenu />
              <Button
                variant="outlined"
                onClick={handleBackToHome}
                sx={{ fontWeight: 500 }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateProject}
                sx={{ fontWeight: 600 }}
              >
                New Project
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Surveying Projects
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track compliance for all your surveying projects
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : projects.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 8,
              textAlign: 'center',
              border: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
            }}
          >
            <FolderOpen sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              No Projects Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              Get started by creating your first surveying project. Track ordinance compliance,
              upload documents, and manage requirements all in one place.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={handleCreateProject}
              sx={{ px: 4, py: 1.5, fontWeight: 600 }}
            >
              Create Your First Project
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {projects.map((project: Project) => (
              <Grid item xs={12} sm={6} lg={4} key={project.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, pb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, mr: 2 }}>
                        {project.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={project.status}
                          color={getStatusColor(project.status) as any}
                          size="small"
                          variant="outlined"
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProject(project);
                            setDeleteDialog(true);
                            setDeleteConfirmText('');
                          }}
                          sx={{
                            color: 'error.main',
                            '&:hover': {
                              bgcolor: 'error.light',
                              color: 'white'
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Chip
                        label={project.ordinance_type}
                        variant="filled"
                        size="small"
                        color="secondary"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>

                    {project.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.5,
                        }}
                      >
                        {project.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto', pt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(project.created_date)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Description sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {project.documents?.length || 0} docs
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForward />}
                      onClick={() => handleOpenProject(project.id)}
                      sx={{ fontWeight: 600, borderRadius: 1.5 }}
                    >
                      Open Project
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <Warning />
          Delete Project
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to delete "<strong>{selectedProject?.name}</strong>"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This action will permanently delete:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 3 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              All project documents and files from S3 storage
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              All extracted requirements and analysis data
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              All processing history and extraction jobs
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              The project record itself
            </Typography>
          </Box>
          <Typography variant="body2" color="error.main" sx={{ mb: 2, fontWeight: 600 }}>
            This action cannot be undone.
          </Typography>
          <TextField
            fullWidth
            label={`Type "DELETE" to confirm`}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
            variant="outlined"
            error={deleteConfirmText !== '' && deleteConfirmText !== 'DELETE'}
            helperText={deleteConfirmText !== '' && deleteConfirmText !== 'DELETE' ? 'Must type "DELETE" exactly' : ''}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteConfirmText !== 'DELETE' || deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}